import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Flex, Text } from '@chakra-ui/react';
import { CloseIcon } from '@chakra-ui/icons';
import { useAppDispatch, useAppSelector } from '../../../store/redux/hooks';
import {
  selectFilterValues,
  selectSortValues,
  updateFilterValues,
  updateSortValues,
  initialSortValues,
  setNewFiltersSet,
} from '../../../store/redux/slices/passList.slice';
import { useListPassTemplatesInfiniteQuery } from '../../../store/redux/api/templateApi';
import { MeContext } from '../../../context/MeContext';
import type { MeContextState } from '../../../store/types/account';
import SearchableDropdownButton from './SearchableDropdownButton';

const STATUS_OPTIONS = [
  { label: 'Expired', value: 'expired' },
  { label: 'Issued', value: 'issued' },
  { label: 'Active', value: 'active' },
  { label: 'Removed', value: 'removed' },
  { label: 'Deactivated', value: 'deactivated' },
];

const SORT_OPTIONS = [
  { label: 'Newest first', sortBy: 'createdAt', sortOrder: 'desc' },
  { label: 'Oldest first', sortBy: 'createdAt', sortOrder: 'asc' },
];

interface QuickFiltersProps {
  onOpenMoreFilters: () => void;
  isMoreFiltersOpen: boolean;
}

function QuickFilters({
  onOpenMoreFilters,
  isMoreFiltersOpen,
}: QuickFiltersProps) {
  const dispatch = useAppDispatch();
  const filterValues = useAppSelector(selectFilterValues);
  const sortValues = useAppSelector(selectSortValues);
  const meCtx = useContext(MeContext) as { state: MeContextState };
  const { filterEntity } = meCtx.state;

  const [templatePage, setTemplatePage] = useState(1);

  const { data: templatesData, isFetching: isTemplatesFetching } =
    useListPassTemplatesInfiniteQuery({
      entityId: filterEntity?.uuid,
      skipImages: true,
      fields: 'name',
      page: templatePage,
      perPage: 100,
    });

  const passTemplatesList = useMemo(() => {
    const items = templatesData?.data;
    if (!items?.length) return [];
    return items.map((item) => ({
      label: item.attributes.name,
      value: item.id,
    }));
  }, [templatesData]);

  const [templateSearch, setTemplateSearch] = useState('');

  const filteredTemplatesList = useMemo(() => {
    if (!templateSearch.trim()) return passTemplatesList;
    const search = templateSearch.trim().toLowerCase();
    return passTemplatesList.filter((template) =>
      template.label.toLowerCase().includes(search)
    );
  }, [passTemplatesList, templateSearch]);

  const hasMoreTemplates = useMemo(() => {
    if (!templatesData) return false;
    const totalCount = templatesData.meta?.totalCount;
    if (typeof totalCount === 'number') {
      return templatesData.data.length < totalCount;
    }
    return false;
  }, [templatesData]);

  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          hasMoreTemplates &&
          !isTemplatesFetching
        ) {
          setTemplatePage((prev) => prev + 1);
        }
      },
      { threshold: 0 }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMoreTemplates, isTemplatesFetching]);

  const selectedTemplates = filterValues.passTemplate || [];
  const selectedStatuses = filterValues.status || [];
  const hasTemplateFilter = selectedTemplates.length > 0;
  const hasStatusFilter = selectedStatuses.length > 0;
  const hasSortApplied = SORT_OPTIONS.some(
    (o) =>
      o.sortBy === sortValues.sortBy && o.sortOrder === sortValues.sortOrder
  );
  const moreFiltersCount =
    [
      filterValues.deviceType,
      filterValues.id,
      filterValues.extId,
      filterValues.extIdPrefix,
      filterValues.addedToWalletDate,
    ].filter(Boolean).length +
    (filterValues.passType?.length ? 1 : 0) +
    (filterValues.groupTag?.length ? 1 : 0);
  const hasMoreFiltersApplied = moreFiltersCount > 0;

  const toggleTemplate = (templateName: string) => {
    const current = selectedTemplates;
    const updated = current.includes(templateName)
      ? current.filter((t) => t !== templateName)
      : [...current, templateName];
    dispatch(updateFilterValues({ ...filterValues, passTemplate: updated }));
    dispatch(setNewFiltersSet(true));
  };

  const clearTemplates = () => {
    dispatch(updateFilterValues({ ...filterValues, passTemplate: [] }));
    dispatch(setNewFiltersSet(true));
  };

  const toggleStatus = (status: string) => {
    const current = selectedStatuses;
    const updated = current.includes(status)
      ? current.filter((s) => s !== status)
      : [...current, status];
    dispatch(updateFilterValues({ ...filterValues, status: updated }));
    dispatch(setNewFiltersSet(true));
  };

  const clearStatuses = () => {
    dispatch(updateFilterValues({ ...filterValues, status: [] }));
    dispatch(setNewFiltersSet(true));
  };

  const clearMoreFilters = () => {
    dispatch(
      updateFilterValues({
        ...filterValues,
        passType: [],
        deviceType: '',
        groupTag: [],
        id: '',
        extId: '',
        addedToWalletDate: '',
        extIdPrefix: '',
      })
    );
    dispatch(setNewFiltersSet(true));
  };

  const handleSortSelect = (key: string) => {
    const option = SORT_OPTIONS.find(
      (o) => `${o.sortBy}-${o.sortOrder}` === key
    );
    if (!option) return;
    dispatch(
      updateSortValues({ sortBy: option.sortBy, sortOrder: option.sortOrder })
    );
  };

  const handleSortClear = () => {
    dispatch(updateSortValues({ ...initialSortValues }));
  };

  const getButtonLabel = (
    defaultLabel: string,
    selected: string[],
    options?: { label: string; value: string }[]
  ) => {
    if (selected.length === 0) return defaultLabel;
    const firstMatch = options?.find((o) => o.value === selected[0]);
    const firstName = firstMatch?.label || selected[0];
    if (selected.length === 1) return firstName;
    return `${firstName}, (+${selected.length - 1})`;
  };

  return (
    <Flex gap="8px" mt="12px" flexWrap="wrap">
      {/* Template filter — searchable multi-select */}
      <SearchableDropdownButton
        buttonLabel={getButtonLabel(
          'Pass template',
          selectedTemplates,
          passTemplatesList
        )}
        isActive={hasTemplateFilter}
        onClear={clearTemplates}
        clearAriaLabel="Clear template filter"
        minW="140px"
        options={filteredTemplatesList.map((template) => ({
          key: template.value,
          label: template.label,
          isSelected: selectedTemplates.includes(template.value),
        }))}
        onOptionClick={toggleTemplate}
        searchValue={templateSearch}
        onSearchChange={setTemplateSearch}
        onMenuClose={() => setTemplateSearch('')}
        emptyMessage="No templates found"
        loadMoreRef={sentinelRef}
        showLoadMore={hasMoreTemplates}
      />

      {/* Status filter — multi-select */}
      <SearchableDropdownButton
        buttonLabel={getButtonLabel('Status', selectedStatuses, STATUS_OPTIONS)}
        isActive={hasStatusFilter}
        onClear={clearStatuses}
        clearAriaLabel="Clear status filter"
        minW="100px"
        options={STATUS_OPTIONS.map((option) => ({
          key: option.value,
          label: option.label,
          isSelected: selectedStatuses.includes(option.value),
        }))}
        onOptionClick={toggleStatus}
      />

      {/* Sort — single select */}
      <SearchableDropdownButton
        buttonLabel={
          hasSortApplied
            ? SORT_OPTIONS.find(
                (o) =>
                  o.sortBy === sortValues.sortBy &&
                  o.sortOrder === sortValues.sortOrder
              )?.label || 'Sort by'
            : 'Sort by'
        }
        isActive={hasSortApplied}
        onClear={handleSortClear}
        clearAriaLabel="Clear sort"
        minW="100px"
        closeOnSelect
        options={SORT_OPTIONS.map((option) => {
          const key = `${option.sortBy}-${option.sortOrder}`;
          return {
            key,
            label: option.label,
            isSelected:
              sortValues.sortBy === option.sortBy &&
              sortValues.sortOrder === option.sortOrder,
          };
        })}
        onOptionClick={handleSortSelect}
      />

      {/* More filters */}
      <Flex align="center" position="relative">
        <Button
          variant="outline"
          size="sm"
          color={hasMoreFiltersApplied ? '#505E69' : '#4C4C4C'}
          borderRadius="5px"
          bg="white"
          fontWeight={400}
          pr={hasMoreFiltersApplied ? '24px' : undefined}
          border={
            isMoreFiltersOpen || hasMoreFiltersApplied
              ? '1.5px solid #505E69'
              : 'none'
          }
          _hover={{ border: '1.5px solid #505E69' }}
          onClick={onOpenMoreFilters}
        >
          <Text textTransform="none" letterSpacing="0.01em">
            {hasMoreFiltersApplied
              ? `More filters (${moreFiltersCount})`
              : 'More filters'}
          </Text>
        </Button>
        {hasMoreFiltersApplied && (
          <CloseIcon
            aria-label="Clear more filters"
            boxSize="9px"
            position="absolute"
            right="10px"
            cursor="pointer"
            zIndex={1}
            color="#989898"
            _hover={{ color: '#4C4C4C' }}
            onClick={clearMoreFilters}
          />
        )}
      </Flex>
    </Flex>
  );
}

export default QuickFilters;
